import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { PrimeReactProvider, PrimeReactContext } from 'primereact/api';
import "@radix-ui/themes/styles.css";
import './AppV2.css';
import { api } from './services/api';
import { Theme } from "@radix-ui/themes";
import { Header } from './componentsV2/Header';
import { useStore } from './store/store';
import { Dashboard } from './componentsV2/Dashboard';
import { Novel } from './componentsV2/Novel';
import { Sidebar } from './componentsV2/Sidebar';
import { Scenes } from './componentsV2/Scenes';
import { Prose } from './componentsV2/Prose';
import { LandingPage } from './componentsV2/LandingPage';
import { SettingsScreen } from './components/SettingsScreen';
import { NavigationHeader } from './componentsV2/NavigationHeader';
import { CreateFiction } from './componentsV2/CreateFiction';
import { ModelSettingsScreen } from './componentsV2/ModelSettingsScreen';
import { CreateNovel } from './componentsV2/CreateNovel';
import { CreateNonFiction } from './componentsV2/CreateNonFiction';
import "primereact/resources/themes/viva-dark/theme.css";

export function AppV2() {
  const AppLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [state, actions] = useStore();
    const showSidebar = location.pathname.includes('/novel/') && !location.pathname.includes('/new');

    const getNovels = async () => {
      const result = await api.getAllNovels();
      actions.setNovels(result.novels);
    }

    const isReadOnly = () => {
      if (!state.user) return true;
      
      // Check if subscription is active
      if (state.user.subscription_active) return false;
      
      // Check if trial period is still valid
      if (state.user.trial_end_date) {
        const trialEndDate = new Date(state.user.trial_end_date);
        const currentDate = new Date();
        return currentDate > trialEndDate;
      }
      
      return true;
    };

    // Fetch the latest subscription data
    const fetchSubscriptionData = async (userData) => {
      try {
        const result = await api.getUserSubscription(userData.id);
        if (result.success) {
          // Update the user data with the latest subscription info
          const updatedUserData = {
            ...userData,
            trial_end_date: result.subscription.trial_end_date,
            subscription_active: result.subscription.subscription_active,
            subscription_id: result.subscription.subscription_id
          };
          
          // Update localStorage with fresh subscription data
          localStorage.setItem('user', JSON.stringify(updatedUserData));
          
          // Update state with the latest user data
          actions.setUser(updatedUserData);
          
          // Update read-only status based on the latest subscription data
          const trialEnded = new Date() > new Date(result.subscription.trial_end_date);
          const isReadOnlyMode = !result.subscription.subscription_active && trialEnded;
          actions.setReadOnly(isReadOnlyMode);
        }
      } catch (error) {
        console.error("Error fetching subscription data:", error);
      }
    };

    useEffect(() => {
      const loggedInUser = localStorage.getItem('user');
      if (loggedInUser) {
        const userData = JSON.parse(loggedInUser);
        actions.setUser(userData);
        
        fetchSubscriptionData(userData);
        getNovels();
        if (location.pathname === '/'){
          navigate('/dashboard');
        }
        
        // Update currentNovel when on a novel-specific page
        if (location.pathname.includes('/novel/')) {
          const pathParts = location.pathname.split('/');
          const novelId = pathParts[2]; // Extract novelId from the URL
          
          const fetchNovel = async () => {
            try {
              const result = await api.getNovelById(novelId);
              const novel = result.novel;
  
              const [characterResult, locationsResult, loreResult, chatsResult, notesResult] = await Promise.all([
                  api.getCharactersByNovelId(novelId),
                  api.getLocationsByNovelId(novelId),
                  api.getLoreItemsByNovelId(novelId),
                  api.getChatsByNovel(novelId),
                  api.getNotesByNovelId(novelId)
              ]);
  
              novel.characters = characterResult.characters;
              novel.locations = locationsResult.locations;
              novel.lore = loreResult.loreItems;
              novel.chats = chatsResult.chats;
              novel.notes = notesResult.notes;

              actions.setCurrentNovel(novel);

            } catch (error) {
              console.error("Error fetching novel:", error);
            }
          };
          
          fetchNovel();
        }
      }
    }, [location.pathname]);

    return (
      <div style={{
        backgroundColor: '#27242c', 
        color: 'white', 
        overflow: 'hidden',
        fontFamily: 'Noto Sans',
        minHeight: '100vh'}}>
        <Header />
        {(
          location.pathname !== '/' && 
          location.pathname !== '/settings/account' && 
          location.pathname !== '/settings/model' &&
          !location.pathname.includes('new')
          ) && <NavigationHeader />}
        <Routes>
          <Route path="/" element={<LandingPage getNovels={getNovels} />} />
          <Route path="/novel/new" element={<CreateNovel />} />
          <Route path="/fiction/new" element={<CreateFiction />} />
          <Route path="/nonfiction/new" element={<CreateNonFiction />} />
          <Route path="/settings/account" element={<SettingsScreen />} />
          <Route path="/settings/model" element={<ModelSettingsScreen />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/novel/:novelId" element={<Novel />} />
          <Route path="/novel/:novelId/chapter/:chapterId" element={<Scenes />} />
          <Route path="/novel/:novelId/chapter/:chapterId/scene/:sceneId" element={<Prose style={showSidebar ? {marginLeft: '20px'} : {}}/>} />
        </Routes>
        {showSidebar && <Sidebar />}
      </div>
    )
  }

  return (
    <div className="App">
      <Router>
        <Theme appearance="dark">
         <PrimeReactProvider>
           <AppLayout/>
         </PrimeReactProvider>
        </Theme>
      </Router>
    </div>
  );
}

export default AppV2;
